import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import {
  Labeled,
  UpdateButton,
  useGetIdentity,
  useGetList,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';

import { NumericOverrideField } from '@/components/NumericOverrideField';
import { SectionPaper } from '@/components/SectionPaper';
import { therapistOverridableFlags } from '@/featureFlags/registry';
import { TherapistFeatureFlagOverrides } from '@/featureFlags/TherapistFeatureFlagOverrides';
import { adminRpcClient } from '@/providers/backendClient';
import { Semaphore } from '@/screens/therapistAppConfig/Semaphore';

import { ForceLogout } from './ForceLogout';
import { NukeLegacyFiles } from './NukeLegacyFiles';
import { SystemSnapshot } from './TherapistsCommandPanel';

type ActionKey = 'flags' | 'debug' | 'appBehavior' | 'remote';

type DialogShellProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
};

const DialogShell = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 'sm',
}: DialogShellProps) => (
  <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent dividers>{children}</DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <Box display="flex" flexDirection="column" gap={0.25} marginBottom={1}>
    <Typography variant="h6">{title}</Typography>
    {description ? (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    ) : null}
  </Box>
);

const ModelOverridesAccordion = () => {
  const items = useModelOverrideItems();
  const hasOverrides = items.length > 0;
  return (
    <Accordion defaultExpanded={hasOverrides} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Model overrides</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <ModelOverridesContent />
      </AccordionDetails>
    </Accordion>
  );
};

const FeatureFlagOverridesDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <DialogShell
    open={open}
    onClose={onClose}
    title="Feature flag & model overrides"
    maxWidth="md"
  >
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <SectionHeader title="Feature flags" />
        <TherapistFeatureFlagOverrides />
      </Box>
      <Divider />
      <ModelOverridesAccordion />
    </Box>
  </DialogShell>
);

const ModelOverridesContent = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();
  const [updateTherapist, { isLoading: isSaving }] = useUpdate(
    'therapists',
    { id: record?.id },
  );

  const updateField = useCallback(
    async (field: string, value: unknown) => {
      if (!record) return;
      await updateTherapist(
        'therapists',
        {
          id: record.id,
          data: {
            [field]: value,
            lastUpdatedAt: new Date(),
            lastUpdatedBy: identity?.id,
          },
        },
        { onSuccess: () => refresh() },
      );
    },
    [updateTherapist, record, identity?.id, refresh],
  );

  const { data: modelsList } = useGetList('llmModels', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const models = Array.isArray(modelsList) ? modelsList : [];
  const processingModels = models.filter(
    (m: { purpose: string }) => m.purpose === 'processing',
  );
  const transcriptModels = models.filter(
    (m: { purpose: string }) => m.purpose === 'transcript',
  );

  const savedDefaultModelId =
    (record as { defaultModelId?: string | null } | undefined)
      ?.defaultModelId ?? null;
  const savedSelect =
    savedDefaultModelId === null || savedDefaultModelId === ''
      ? '__global__'
      : savedDefaultModelId;

  const [pendingLlmSelect, setPendingLlmSelect] = useState(savedSelect);
  const [llmConfirmOpen, setLlmConfirmOpen] = useState(false);

  const savedDefaultTranscriptModelId =
    (
      record as
        | { defaultTranscriptModelId?: string | null }
        | undefined
    )?.defaultTranscriptModelId ?? null;
  const savedTranscriptSelect =
    savedDefaultTranscriptModelId === null ||
    savedDefaultTranscriptModelId === ''
      ? '__global__'
      : savedDefaultTranscriptModelId;

  const [pendingTranscriptSelect, setPendingTranscriptSelect] =
    useState(savedTranscriptSelect);
  const [transcriptConfirmOpen, setTranscriptConfirmOpen] =
    useState(false);

  const getModelName = (list: typeof models, id: string) =>
    list.find(
      (m: { id?: string; _id?: string; name: string }) =>
        (m.id ?? m._id) === id,
    )?.name ?? id;

  const hasLlmChanges = pendingLlmSelect !== savedSelect;
  const pendingLlmName =
    pendingLlmSelect === '__global__'
      ? 'Use global default'
      : getModelName(processingModels, pendingLlmSelect);
  const savedLlmName =
    savedSelect === '__global__'
      ? 'Use global default'
      : getModelName(processingModels, savedSelect);

  const hasTranscriptChanges =
    pendingTranscriptSelect !== savedTranscriptSelect;
  const pendingTranscriptName =
    pendingTranscriptSelect === '__global__'
      ? 'Use global default'
      : getModelName(transcriptModels, pendingTranscriptSelect);
  const savedTranscriptName =
    savedTranscriptSelect === '__global__'
      ? 'Use global default'
      : getModelName(transcriptModels, savedTranscriptSelect);

  const handleLlmSaveClick = useCallback(
    () => setLlmConfirmOpen(true),
    [],
  );
  const handleLlmConfirmClose = useCallback(
    () => setLlmConfirmOpen(false),
    [],
  );
  const handleLlmConfirmSave = useCallback(async () => {
    setLlmConfirmOpen(false);
    const value =
      pendingLlmSelect === '__global__' ? null : pendingLlmSelect;
    await updateField('defaultModelId', value);
  }, [updateField, pendingLlmSelect]);

  const handleTranscriptSaveClick = useCallback(
    () => setTranscriptConfirmOpen(true),
    [],
  );
  const handleTranscriptConfirmClose = useCallback(
    () => setTranscriptConfirmOpen(false),
    [],
  );
  const handleTranscriptConfirmSave = useCallback(async () => {
    setTranscriptConfirmOpen(false);
    const value =
      pendingTranscriptSelect === '__global__'
        ? null
        : pendingTranscriptSelect;
    await updateField('defaultTranscriptModelId', value);
  }, [updateField, pendingTranscriptSelect]);

  useEffect(() => {
    setPendingLlmSelect(savedSelect);
  }, [savedSelect]);

  useEffect(() => {
    setPendingTranscriptSelect(savedTranscriptSelect);
  }, [savedTranscriptSelect]);

  if (!record) return null;

  return (
    <Box display="flex" flexDirection="column" gap="1.5rem">
        <Box>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            LLM model
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginBottom: 1 }}
          >
            Override the default GPT/LLM model for this therapist.
          </Typography>
          <FormControl
            size="small"
            disabled={isSaving}
            sx={{ minWidth: 220 }}
          >
            <InputLabel>Model</InputLabel>
            <Select
              value={pendingLlmSelect}
              label="Model"
              onChange={e => setPendingLlmSelect(e.target.value)}
            >
              <MenuItem value="__global__">Use global default</MenuItem>
              {processingModels.map(
                (m: { id?: string; _id?: string; name: string }) => (
                  <MenuItem key={m.id ?? m._id} value={m.id ?? m._id}>
                    {m.name}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          {hasLlmChanges && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={isSaving}
                onClick={handleLlmSaveClick}
              >
                Save
              </Button>
            </Box>
          )}
          <Dialog
            open={llmConfirmOpen}
            onClose={handleLlmConfirmClose}
          >
            <DialogTitle>Change LLM model override?</DialogTitle>
            <DialogContent>
              <Typography>
                {savedSelect === '__global__'
                  ? `Set this therapist's model to "${pendingLlmName}"? They will use this model instead of the global default.`
                  : pendingLlmSelect === '__global__'
                    ? `Clear the override (currently "${savedLlmName}")? This therapist will use the global default model.`
                    : `Change this therapist's model from "${savedLlmName}" to "${pendingLlmName}"?`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleLlmConfirmClose}>Cancel</Button>
              <Button
                onClick={handleLlmConfirmSave}
                color="primary"
                variant="contained"
                disabled={isSaving}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Transcription model
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginBottom: 1 }}
          >
            Override the default speech-to-text model for this therapist
            (llmModels with purpose &quot;transcript&quot;).
          </Typography>
          <FormControl
            size="small"
            disabled={isSaving}
            sx={{ minWidth: 220 }}
          >
            <InputLabel>Transcript model</InputLabel>
            <Select
              value={pendingTranscriptSelect}
              label="Transcript model"
              onChange={e => setPendingTranscriptSelect(e.target.value)}
            >
              <MenuItem value="__global__">Use global default</MenuItem>
              {transcriptModels.map(
                (m: { id?: string; _id?: string; name: string }) => (
                  <MenuItem key={m.id ?? m._id} value={m.id ?? m._id}>
                    {m.name}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          {hasTranscriptChanges && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={isSaving}
                onClick={handleTranscriptSaveClick}
              >
                Save
              </Button>
            </Box>
          )}
          <Dialog
            open={transcriptConfirmOpen}
            onClose={handleTranscriptConfirmClose}
          >
            <DialogTitle>
              Change transcription model override?
            </DialogTitle>
            <DialogContent>
              <Typography>
                {savedTranscriptSelect === '__global__'
                  ? `Set this therapist's transcription model to "${pendingTranscriptName}"? They will use this model instead of the global default.`
                  : pendingTranscriptSelect === '__global__'
                    ? `Clear the override (currently "${savedTranscriptName}")? This therapist will use the global default transcription model.`
                    : `Change this therapist's transcription model from "${savedTranscriptName}" to "${pendingTranscriptName}"?`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTranscriptConfirmClose}>
                Cancel
              </Button>
              <Button
                onClick={handleTranscriptConfirmSave}
                color="primary"
                variant="contained"
                disabled={isSaving}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
  );
};

const DebugModeContent = () => {
  const record = useRecordContext();
  const refresh = useRefresh();

  if (!record) return null;

  return (
    <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
      <Labeled label="Debug Menu Enabled">
          <Box sx={{ display: 'inline-block' }}>
            <UpdateButton
              resource="therapists"
              label={
                record.isTestTherapist
                  ? 'Disable Debug Menu'
                  : 'Enable Debug Menu'
              }
              type="button"
              color={record.isTestTherapist ? 'error' : 'warning'}
              variant="contained"
              mutationMode="pessimistic"
              confirmTitle="Are you sure?"
              confirmContent={
                record.isTestTherapist
                  ? 'This will Disable Debug Menu access for this user'
                  : 'This will Enable Debug Menu access for this user'
              }
              icon={
                record.isTestTherapist ? <ClearIcon /> : <CheckIcon />
              }
              sx={{ color: '#fff', width: 'fit-content' }}
              mutationOptions={{ onSuccess: refresh }}
              data={{
                isTestTherapist: !record.isTestTherapist,
                ...(record.isTestTherapist && {
                  advancedDebugMenuEnabled: false,
                }),
              }}
            />
          </Box>
        </Labeled>
        {record.isTestTherapist && (
          <Labeled label="Advanced Debug Mode">
            <Box sx={{ display: 'inline-block' }}>
              <UpdateButton
                resource="therapists"
                label={
                  record.advancedDebugMenuEnabled
                    ? 'Disable Advanced Debug Mode'
                    : 'Enable Advanced Debug Mode'
                }
                type="button"
                color={
                  record.advancedDebugMenuEnabled ? 'error' : 'warning'
                }
                variant="contained"
                mutationMode="pessimistic"
                confirmTitle="Are you sure?"
                confirmContent={
                  record.advancedDebugMenuEnabled
                    ? 'This will Disable Advanced Debug Mode access for this user'
                    : 'This will Enable Advanced Debug Mode access for this user'
                }
                icon={
                  record.advancedDebugMenuEnabled ? (
                    <ClearIcon />
                  ) : (
                    <CheckIcon />
                  )
                }
                sx={{ color: '#fff', width: 'fit-content' }}
                mutationOptions={{ onSuccess: refresh }}
                data={{
                  advancedDebugMenuEnabled:
                    !record.advancedDebugMenuEnabled,
                }}
              />
            </Box>
          </Labeled>
        )}
        <Labeled label="Show last archived global alert">
          <Box sx={{ display: 'inline-block' }}>
            <UpdateButton
              resource="therapists"
              label={
                record.debug_showLastArchivedGlobalAlert
                  ? 'Disable Show Last Archived Global Alert'
                  : 'Enable Show Last Archived Global Alert'
              }
              type="button"
              color={
                record.debug_showLastArchivedGlobalAlert
                  ? 'error'
                  : 'warning'
              }
              variant="contained"
              mutationMode="pessimistic"
              confirmTitle="Are you sure?"
              confirmContent={
                record.debug_showLastArchivedGlobalAlert
                  ? 'This will stop showing the last archived global alert for this user.'
                  : 'This will show the last global alert even when archived (for debugging).'
              }
              icon={
                record.debug_showLastArchivedGlobalAlert ? (
                  <ClearIcon />
                ) : (
                  <CheckIcon />
                )
              }
              sx={{ color: '#fff', width: 'fit-content' }}
              mutationOptions={{ onSuccess: refresh }}
              data={{
                debug_showLastArchivedGlobalAlert:
                  !record.debug_showLastArchivedGlobalAlert,
              }}
            />
          </Box>
        </Labeled>
      </Box>
  );
};

const OtaUpdatesContent = () => {
  const record = useRecordContext();
  const refresh = useRefresh();

  if (!record) return null;

  return (
    <Labeled label="Mandatory OTA Updates">
        <Box sx={{ display: 'inline-block' }}>
          <UpdateButton
            resource="therapists"
            label={
              record.otaUpdatesMandatory
                ? 'Disable Mandatory OTA Updates'
                : 'Force OTA Updates'
            }
            type="button"
            color={record.otaUpdatesMandatory ? 'error' : 'warning'}
            variant="contained"
            mutationMode="pessimistic"
            confirmTitle="Are you sure?"
            confirmContent={
              record.otaUpdatesMandatory
                ? 'This will Disable Mandatory OTA Updates for this therapist. This can be overridden by the global OTA settings.'
                : 'This will Force the Therapist to update their app as soon as an OTA is available'
            }
            icon={
              record.otaUpdatesMandatory ? <ClearIcon /> : <CheckIcon />
            }
            sx={{ color: '#fff', width: 'fit-content' }}
            mutationOptions={{ onSuccess: refresh }}
            data={{
              otaUpdatesMandatory: record.otaUpdatesMandatory ? 0 : 1,
            }}
          />
        </Box>
      </Labeled>
  );
};

const AdminConfigContent = () => {
  const record = useRecordContext();
  const refresh = useRefresh();

  const {
    mutate: toggleHiddenTestAccount,
    isPending: isTogglingHidden,
  } = useMutation({
    mutationKey: [
      'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
      record?.id,
      record?.isHiddenTestAccount,
    ],
    mutationFn: async () => {
      await adminRpcClient(
        'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
        [record!.id, !Boolean(record!.isHiddenTestAccount)],
      );
    },
    onSettled: refresh,
  });

  if (!record) return null;

  return (
    <Labeled label="Hidden Test Account">
        <Box sx={{ display: 'inline-block' }}>
          <Button
            color={!record.isHiddenTestAccount ? 'warning' : 'error'}
            variant="contained"
            sx={{ color: '#fff', width: 'fit-content' }}
            size="small"
            disabled={isTogglingHidden}
            startIcon={
              isTogglingHidden ? (
                <CircularProgress size={18} />
              ) : record.isHiddenTestAccount ? (
                <ClearIcon />
              ) : (
                <CheckIcon />
              )
            }
            onClick={() => toggleHiddenTestAccount()}
          >
            {record.isHiddenTestAccount
              ? 'Remove From Hidden Test Accounts'
              : 'Add to Hidden Test Accounts'}
          </Button>
        </Box>
      </Labeled>
  );
};

const APP_BEHAVIOR_FIELDS: readonly {
  field: string;
  label: string;
  helperText: string;
  inputProps?: Record<string, unknown>;
}[] = [
  {
    field: 'freeTrialPopupPlacement',
    label: 'Free Trial Popup Placement',
    helperText:
      'Number of remaining free trial notes when popup should appear. Leave empty to use global config. Use 0 or negative to disable.',
    inputProps: { step: 1 },
  },
  {
    field: 'appReviewRequestPlacement',
    label: 'App Review Request Placement',
    helperText:
      'Number indicating when to request app review. Leave empty to use global config.',
    inputProps: { step: 1 },
  },
  {
    field: 'tracing_sampleRate_global',
    label: 'Global Tracing Sample Rate',
    helperText:
      'Global tracing sample rate (0.0 to 1.0). Leave empty to use global config.',
    inputProps: { step: 0.01, min: 0, max: 1 },
  },
  {
    field: 'tracing_sampleRate_local',
    label: 'Local Tracing Sample Rate',
    helperText:
      'Local tracing sample rate (0.0 to 1.0). Leave empty to use global config.',
    inputProps: { step: 0.01, min: 0, max: 1 },
  },
];

const AppBehaviorContent = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5} marginBottom={1}>
        <Typography variant="body2" color="text.secondary">
          Override numeric app-config values. Leave empty to use global
          config.
        </Typography>
        <Tooltip title="Each override applies to this therapist only.">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
        {APP_BEHAVIOR_FIELDS.map(meta => (
          <NumericOverrideField
            key={meta.field}
            field={meta.field}
            label={meta.label}
            helperText={meta.helperText}
            inputProps={meta.inputProps}
          />
        ))}
      </Box>
    </Box>
  );
};

const DebugModeDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <DialogShell
    open={open}
    onClose={onClose}
    title="Debug Mode & Admin Config"
    maxWidth="md"
  >
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <SectionHeader title="Debug Mode" />
        <DebugModeContent />
      </Box>
      <Divider />
      <Box>
        <SectionHeader title="Admin Config" />
        <AdminConfigContent />
      </Box>
    </Box>
  </DialogShell>
);

const AppBehaviorDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <DialogShell
    open={open}
    onClose={onClose}
    title="App Behavior & OTA Updates"
    maxWidth="md"
  >
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <SectionHeader title="App Behavior" />
        <AppBehaviorContent />
      </Box>
      <Divider />
      <Box>
        <SectionHeader title="OTA Updates" />
        <OtaUpdatesContent />
      </Box>
    </Box>
  </DialogShell>
);

const RemoteCommandsDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <DialogShell
    open={open}
    onClose={onClose}
    title="Remote Commands"
    maxWidth="md"
  >
    <Box display="flex" flexDirection="column" gap="2rem">
      <Box>
        <h3>System Snapshot</h3>
        <SystemSnapshot />
      </Box>
      <Box>
        <h3>Delete Legacy Files</h3>
        <NukeLegacyFiles />
      </Box>
      <Box>
        <h3>Force Logout/Reset</h3>
        <ForceLogout />
      </Box>
    </Box>
  </DialogShell>
);

type OverrideItem = {
  label: string;
  /** When set, renders a Semaphore indicator instead of a value chip. */
  semaphore?: boolean;
  /** Used when `semaphore` is not set. */
  value?: string;
};

const isOverrideNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const isOverrideTruthy = (v: unknown): boolean => v === true || v === 1;

const isOverrideDefined = (v: unknown): boolean =>
  v !== null && v !== undefined && v !== '';

const NoOverrides = ({ message }: { message?: string }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ fontStyle: 'italic' }}
  >
    {message ?? 'No overrides — using global defaults.'}
  </Typography>
);

const OverrideList = ({ items }: { items: OverrideItem[] }) => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    {items.map(item => (
      <Box
        key={item.label}
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {item.label}
        </Typography>
        {item.semaphore ? (
          <Semaphore enabled />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={item.value}
          />
        )}
      </Box>
    ))}
  </Box>
);

const useFeatureFlagOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  if (!record) return [];
  const rec = record as Record<string, unknown>;

  const items: OverrideItem[] = [];
  for (const flag of therapistOverridableFlags) {
    if (!flag.therapistField) continue;
    const value = rec[flag.therapistField];
    if (flag.kind === 'boolean') {
      if (isOverrideTruthy(value)) {
        items.push({ label: flag.label, semaphore: true });
      }
    } else {
      if (isOverrideDefined(value)) {
        items.push({ label: flag.label, value: String(value) });
      }
    }
  }

  const minWords = rec.transcriptionSpeakerEnrollmentMinWordsPerSegment;
  if (isOverrideNumber(minWords)) {
    items.push({
      label: 'Enrollment min words per diarized segment',
      value: String(minWords),
    });
  }

  return items;
};

const useModelOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  const { data: modelsList } = useGetList('llmModels', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  if (!record) return [];
  const rec = record as Record<string, unknown>;

  const models = Array.isArray(modelsList) ? modelsList : [];
  const getName = (id: string) =>
    models.find(
      (m: { id?: string; _id?: string; name: string }) =>
        (m.id ?? m._id) === id,
    )?.name ?? id;

  const items: OverrideItem[] = [];
  const llm = rec.defaultModelId;
  if (typeof llm === 'string' && llm) {
    items.push({ label: 'LLM model', value: getName(llm) });
  }
  const transcript = rec.defaultTranscriptModelId;
  if (typeof transcript === 'string' && transcript) {
    items.push({
      label: 'Transcription model',
      value: getName(transcript),
    });
  }

  return items;
};

const useDebugModeOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  if (!record) return [];

  const items: OverrideItem[] = [];
  if (isOverrideTruthy(record.isTestTherapist)) {
    items.push({ label: 'Debug Menu', semaphore: true });
  }
  if (isOverrideTruthy(record.advancedDebugMenuEnabled)) {
    items.push({ label: 'Advanced Debug Mode', semaphore: true });
  }
  if (isOverrideTruthy(record.debug_showLastArchivedGlobalAlert)) {
    items.push({
      label: 'Show last archived global alert',
      semaphore: true,
    });
  }

  return items;
};

const useAdminConfigOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  if (!record) return [];

  const items: OverrideItem[] = [];
  if (isOverrideTruthy(record.isHiddenTestAccount)) {
    items.push({ label: 'Hidden Test Account', semaphore: true });
  }

  return items;
};

const useOtaUpdatesOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  if (!record) return [];

  const items: OverrideItem[] = [];
  if (isOverrideTruthy(record.otaUpdatesMandatory)) {
    items.push({ label: 'Mandatory OTA Updates', semaphore: true });
  }

  return items;
};

const useAppBehaviorOverrideItems = (): OverrideItem[] => {
  const record = useRecordContext();
  if (!record) return [];
  const rec = record as Record<string, unknown>;

  const items: OverrideItem[] = [];
  for (const meta of APP_BEHAVIOR_FIELDS) {
    const value = rec[meta.field];
    if (isOverrideNumber(value)) {
      items.push({ label: meta.label, value: String(value) });
    }
  }

  return items;
};

const FeatureFlagAndModelSummary = () => {
  const items = [
    ...useFeatureFlagOverrideItems(),
    ...useModelOverrideItems(),
  ];
  return items.length === 0 ? <NoOverrides /> : <OverrideList items={items} />;
};

const DebugAndAdminSummary = () => {
  const items = [
    ...useDebugModeOverrideItems(),
    ...useAdminConfigOverrideItems(),
  ];
  return items.length === 0 ? <NoOverrides /> : <OverrideList items={items} />;
};

const AppBehaviorAndOtaSummary = () => {
  const items = [
    ...useAppBehaviorOverrideItems(),
    ...useOtaUpdatesOverrideItems(),
  ];
  return items.length === 0 ? <NoOverrides /> : <OverrideList items={items} />;
};

const RemoteCommandsSummary = () => (
  <Typography variant="body2" color="text.secondary">
    Available actions: System Snapshot, Delete Legacy Files, Force
    Logout / Reset.
  </Typography>
);

type ActionCardProps = {
  title: string;
  changeLabel?: string;
  onChange: () => void;
  children: React.ReactNode;
};

const ActionCard = ({
  title,
  changeLabel = 'Change',
  onChange,
  children,
}: ActionCardProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      padding: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      flex: '1 1 280px',
      minWidth: 260,
      maxWidth: 420,
    }}
  >
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
    <Box>
      <Button
        variant="outlined"
        size="small"
        onClick={onChange}
        sx={{ textTransform: 'none' }}
      >
        {changeLabel}
      </Button>
    </Box>
  </Box>
);

export const TherapistActionsPanel = () => {
  const [openKey, setOpenKey] = useState<ActionKey | null>(null);

  const onOpen = useCallback((key: ActionKey) => setOpenKey(key), []);
  const onClose = useCallback(() => setOpenKey(null), []);

  return (
    <SectionPaper>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={1}
        marginBottom="0.75rem"
      >
        <Typography variant="h6">Overrides & Actions</Typography>
        <Typography variant="body2" color="text.secondary">
          Read-only view of this therapist&apos;s overrides. Use{' '}
          <strong>Change</strong> on each card to edit.
        </Typography>
      </Box>

      <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1.25}>
        <ActionCard
          title="Feature flag & model overrides"
          onChange={() => onOpen('flags')}
        >
          <FeatureFlagAndModelSummary />
        </ActionCard>

        <ActionCard
          title="Debug Mode & Admin Config"
          onChange={() => onOpen('debug')}
        >
          <DebugAndAdminSummary />
        </ActionCard>

        <ActionCard
          title="App Behavior & OTA Updates"
          onChange={() => onOpen('appBehavior')}
        >
          <AppBehaviorAndOtaSummary />
        </ActionCard>

        <ActionCard
          title="Remote Commands"
          changeLabel="Open"
          onChange={() => onOpen('remote')}
        >
          <RemoteCommandsSummary />
        </ActionCard>
      </Box>

      <FeatureFlagOverridesDialog
        open={openKey === 'flags'}
        onClose={onClose}
      />
      <DebugModeDialog open={openKey === 'debug'} onClose={onClose} />
      <AppBehaviorDialog
        open={openKey === 'appBehavior'}
        onClose={onClose}
      />
      <RemoteCommandsDialog
        open={openKey === 'remote'}
        onClose={onClose}
      />
    </SectionPaper>
  );
};
